import { useEffect, useRef, useState } from 'react';
import './App.css';
import './styles/review-flow.css';
import './styles/finding-detail.css';
import './styles/integrations.css';
import './styles/auth-landing.css';
import './styles/pipeline.css';
import './styles/coverage-board.css';
import './styles/shell-overhaul.css';
import brandLogoLight from './brand-logo.png';
import appFavicon from './favicon.ico';
import {
  activeFindingFilterChips,
  architectureDisplayMode,
  componentMetricValue,
  findingsHref,
  loadCachedValue,
  navLinkClass,
  qualityReasonLabel,
  readCachedValue,
  roleCanOperateRuntimeSources,
  runtimeSourceWindowLabel,
  scopedCacheKey,
} from './appHelpers';
import { defaultHash, parseRoute, routeSubtitle, routeTitle } from './routes';
import {
  backfillSourceConnection,
  buildLoginHref,
  createAlphaTenant,
  createFindingEvalPack,
  createMembership,
  createImprovementPlanForFinding,
  deleteMembership,
  deleteSystem,
  fetchAuditEvents,
  fetchAuthMe,
  fetchArchitectureData,
  fetchCompareWorkspaceData,
  fetchComponentDetailData,
  fetchComponentsData,
  fetchEvalPackDetailData,
  fetchFindingEvalPackPreview,
  fetchFindingComparisonData,
  fetchFindingDetailData,
  fetchFindingsListData,
  fetchMemberships,
  fetchOnboardingStatus,
  fetchOverviewData,
  fetchRecommendationDetailData,
  fetchReleaseMetadata,
  fetchRunTraceData,
  fetchRuntimeRiskCoverageBoard,
  fetchSystem,
  fetchSystemFindingsData,
  fetchSystems,
  fetchWorkflowDetailData,
  fetchWorkflowSummariesData,
  isAuthError,
  logout,
  onboardSystem,
  replayIngestion,
  switchActiveTenant,
  updateImprovementPlan,
  updateMembership,
} from './data';
import ArchitectureGraph from './components/ArchitectureGraph';
import { CompactMetric, DeleteConfirmDialog, OnboardingBanner, PipelineStrip, StatusCard } from './components/AppShellPrimitives';
import AccessView from './views/AccessView';
import ArchitectureView from './views/ArchitectureView';
import AuthLandingView from './views/AuthLandingView';
import CompareView from './views/CompareView';
import ComponentsCatalogView from './views/ComponentsCatalogView';
import ComponentDetailView from './views/ComponentDetailView';
import EvalPackDetailView from './views/EvalPackDetailView';
import FindingDetailView from './views/FindingDetailView';
import FindingsListView from './views/FindingsListView';
import HowRtiWorksView from './views/HowRtiWorksView';
import IntegrationsView from './views/IntegrationsView';
import OnboardingView from './views/OnboardingView';
import OverviewView from './views/OverviewView';
import RecommendationDetailView from './views/RecommendationDetailView';
import RunTraceDetailView from './views/RunTraceDetailView';
import RuntimeRiskCoverageBoardView from './views/RuntimeRiskCoverageBoardView';
import SystemContextStrip from './views/SystemContextStrip';
import SystemsPortfolioView from './views/SystemsPortfolioView';
import WorkflowsListView from './views/WorkflowsListView';
import WorkflowDetailView from './views/WorkflowDetailView';

const ACTIVE_FINDING_STATUSES = new Set(['open', 'in_progress']);
const ONBOARDING_TERMINAL_STATUSES = new Set(['completed', 'failed']);
const ONBOARDING_POLL_ROUTES = new Set([
  'overview',
  'compare',
  'workflows-list',
  'workflow-detail',
  'runtime-coverage',
  'findings-list',
  'finding-detail',
  'eval-pack-detail',
  'run-trace-detail',
]);
const ONBOARDING_STALL_THRESHOLD_MS = 60000;
const ONBOARDING_FAST_POLL_MS = 2500;
const ONBOARDING_MEDIUM_POLL_MS = 5000;
const ONBOARDING_SLOW_POLL_MS = 10000;
const RELIABILITY_SEVERITY_ORDER = {
  critical: 4,
  high: 3,
  medium: 2,
  low: 1,
};

function formatReleaseDate(value) {
  if (!value) {
    return 'Deploy time unavailable';
  }
  const parsedDate = new Date(value);
  if (Number.isNaN(parsedDate.getTime())) {
    return value;
  }
  return new Intl.DateTimeFormat(undefined, {
    month: 'short',
    day: 'numeric',
    year: 'numeric',
    hour: '2-digit',
    minute: '2-digit',
  }).format(parsedDate);
}

function compactSubtitle(route) {
  const subtitle = routeSubtitle(route);
  const [firstSentence] = subtitle.split(/(?<=\.)\s+/);
  return firstSentence || subtitle;
}

function pipelineStatusForRoute(routeName, stageRoutes, fallbackStatus = 'pending') {
  return stageRoutes.includes(routeName) ? 'current' : fallbackStatus;
}

function buildSystemPipelineSteps(selectedSystem, route, counts) {
  if (!selectedSystem?.id) {
    return [];
  }
  const systemId = selectedSystem.id;
  const activeFindingCount = counts.activeFindingCount || 0;
  const coverageCount = counts.coverageCount || 0;
  const evidencePostureState = selectedSystem.portfolio_summary?.evidence_posture?.state || 'unknown';
  const evidenceNeedsAttention = ['stale', 'missing', 'unknown'].includes(evidencePostureState);
  const findingsStatus = pipelineStatusForRoute(
    route.name,
    ['findings-list', 'finding-detail', 'recommendation-detail', 'eval-pack-detail'],
    activeFindingCount ? 'attention' : 'pending'
  );
  const coverageStatus = pipelineStatusForRoute(
    route.name,
    ['runtime-coverage'],
    coverageCount ? 'attention' : 'pending'
  );
  const proofStatus = pipelineStatusForRoute(route.name, ['compare'], coverageCount ? 'attention' : 'pending');

  return [
    {
      key: 'evidence',
      kicker: 'Evidence',
      label: 'Runtime',
      meta: evidenceNeedsAttention ? 'Refresh or upload evidence' : 'Evidence posture visible',
      href: `#/systems/${systemId}`,
      status: pipelineStatusForRoute(route.name, ['overview', 'workflows-list', 'workflow-detail', 'run-trace-detail', 'integrations'], evidenceNeedsAttention ? 'attention' : 'complete'),
      statusLabel: evidenceNeedsAttention ? 'Check' : 'Ready',
      current: ['overview', 'workflows-list', 'workflow-detail', 'run-trace-detail', 'integrations'].includes(route.name),
    },
    {
      key: 'triage',
      kicker: 'Triage',
      label: 'Findings',
      meta: ['finding-detail', 'eval-pack-detail'].includes(route.name)
        ? 'Scenario review branch open'
        : `${activeFindingCount} active ${activeFindingCount === 1 ? 'finding' : 'findings'}`,
      href: `#/systems/${systemId}/findings`,
      status: findingsStatus,
      statusLabel: route.name.includes('finding') ? 'Current' : activeFindingCount ? 'Review' : 'Clear',
      current: ['findings-list', 'finding-detail', 'recommendation-detail'].includes(route.name),
    },
    {
      key: 'coverage',
      kicker: 'Eval',
      label: 'Coverage',
      meta: coverageCount ? `${coverageCount} active runtime ${coverageCount === 1 ? 'risk' : 'risks'}` : 'Track sent and imported outcomes',
      href: `#/systems/${systemId}/runtime-coverage`,
      status: coverageStatus,
      statusLabel: route.name === 'runtime-coverage' ? 'Current' : coverageCount ? 'Track' : 'Track',
      current: route.name === 'runtime-coverage',
    },
    {
      key: 'proof',
      kicker: 'Outcome',
      label: 'Proof',
      meta: coverageCount ? 'Compare runtime and validation proof' : 'Needs evaluated risks',
      href: `#/systems/${systemId}/compare`,
      status: proofStatus,
      statusLabel: route.name === 'compare' ? 'Current' : coverageCount ? 'Open proof' : 'Waiting',
      current: route.name === 'compare',
    },
  ];
}

function reliabilityIssueBand(severity) {
  const normalizedSeverity = (severity || '').trim().toLowerCase();
  if (normalizedSeverity === 'critical' || normalizedSeverity === 'high') {
    return 'high';
  }
  if (normalizedSeverity === 'medium') {
    return 'medium';
  }
  return 'low';
}

function buildComponentReliabilityIssueMap(findings) {
  const issuesByComponent = new Map();

  findings.forEach((finding) => {
    const componentId = finding.component?.id || finding.component_id;
    if (!componentId) {
      return;
    }
    if ((finding.primary_impact_category || '').trim().toLowerCase() !== 'reliability') {
      return;
    }
    if (!ACTIVE_FINDING_STATUSES.has(finding.status)) {
      return;
    }

    const normalizedSeverity = (finding.severity || 'low').trim().toLowerCase();
    const nextScore = RELIABILITY_SEVERITY_ORDER[normalizedSeverity] || 0;
    const existingIssue = issuesByComponent.get(componentId);

    if (!existingIssue) {
      issuesByComponent.set(componentId, {
        count: 1,
        highestSeverity: normalizedSeverity,
        band: reliabilityIssueBand(normalizedSeverity),
      });
      return;
    }

    const existingScore = RELIABILITY_SEVERITY_ORDER[existingIssue.highestSeverity] || 0;
    issuesByComponent.set(componentId, {
      count: existingIssue.count + 1,
      highestSeverity: nextScore > existingScore ? normalizedSeverity : existingIssue.highestSeverity,
      band: reliabilityIssueBand(nextScore > existingScore ? normalizedSeverity : existingIssue.highestSeverity),
    });
  });

  return issuesByComponent;
}

async function fetchCompareWorkflowSummaries(systemId) {
  try {
    return { value: await fetchWorkflowSummariesData(systemId), warning: null };
  } catch (error) {
    const detail = error?.detail || error?.message || 'Failed to load workflow summaries.';
    return { value: [], warning: `Workflow reliability summaries could not load: ${detail}` };
  }
}

function onboardingPollDelay(elapsedMs) {
  if (elapsedMs < 15000) {
    return ONBOARDING_FAST_POLL_MS;
  }
  if (elapsedMs < ONBOARDING_STALL_THRESHOLD_MS) {
    return ONBOARDING_MEDIUM_POLL_MS;
  }
  return ONBOARDING_SLOW_POLL_MS;
}

function sameJsonValue(left, right) {
  return JSON.stringify(left || null) === JSON.stringify(right || null);
}

function App() {
  const requestCacheRef = useRef(new Map());
  const moreNavRef = useRef(null);
  const [route, setRoute] = useState(() => parseRoute(window.location.hash));
  const [moreNavOpen, setMoreNavOpen] = useState(false);
  const [auth, setAuth] = useState(null);
  const [releaseMetadata, setReleaseMetadata] = useState(null);
  const [authLoading, setAuthLoading] = useState(true);
  const [authFailure, setAuthFailure] = useState(null);
  const [authRefreshSeed] = useState(0);
  const [memberships, setMemberships] = useState([]);
  const [auditEvents, setAuditEvents] = useState([]);
  const [accessLoading, setAccessLoading] = useState(false);
  const [accessError, setAccessError] = useState('');
  const [tenantSwitching, setTenantSwitching] = useState(false);
  const [logoutSubmitting, setLogoutSubmitting] = useState(false);
  const [systems, setSystems] = useState([]);
  const [systemsLoading, setSystemsLoading] = useState(false);
  const [systemsLoaded, setSystemsLoaded] = useState(false);
  const [systemsError, setSystemsError] = useState('');
  const [routeSystem, setRouteSystem] = useState(null);
  const [routeSystemMissing, setRouteSystemMissing] = useState(false);
  const [overview, setOverview] = useState(null);
  const [architecture, setArchitecture] = useState(null);
  const [componentsList, setComponentsList] = useState([]);
  const [componentDetail, setComponentDetail] = useState(null);
  const [findings, setFindings] = useState([]);
  const [findingWorkflows, setFindingWorkflows] = useState([]);
  const [findingComponents, setFindingComponents] = useState([]);
  const [findingDetail, setFindingDetail] = useState(null);
  const [findingComparison, setFindingComparison] = useState(null);
  const [evalPackDetail, setEvalPackDetail] = useState(null);
  const [runTrace, setRunTrace] = useState(null);
  const [evalPackPreview, setEvalPackPreview] = useState(null);
  const [evalPackPreviewFindingId, setEvalPackPreviewFindingId] = useState(null);
  const [evalPackPreviewLoadingFindingId, setEvalPackPreviewLoadingFindingId] = useState(null);
  const [evalPackActionLoadingFindingId, setEvalPackActionLoadingFindingId] = useState(null);
  const [evalPackActionError, setEvalPackActionError] = useState('');
  const [recommendationDetail, setRecommendationDetail] = useState(null);
  const [workflowSummaries, setWorkflowSummaries] = useState([]);
  const [workflowDetail, setWorkflowDetail] = useState(null);
  const [runtimeCoverageBoard, setRuntimeCoverageBoard] = useState(null);
  const [compareSummary, setCompareSummary] = useState(null);
  const [compareFindings, setCompareFindings] = useState([]);
  const [compareWorkflows, setCompareWorkflows] = useState([]);
  const [compareComponents, setCompareComponents] = useState([]);
  const [compareWarnings, setCompareWarnings] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [refreshSeed, setRefreshSeed] = useState(0);
  const [onboarding, setOnboarding] = useState(null);
  const [onboardingStalled, setOnboardingStalled] = useState(false);
  const [onboardName, setOnboardName] = useState('');
  const [onboardSourceType, setOnboardSourceType] = useState('smart_trace');
  const [onboardDescription, setOnboardDescription] = useState('');
  const [onboardFile, setOnboardFile] = useState(null);
  const [includeBaselineUpload, setIncludeBaselineUpload] = useState(false);
  const [baselineSourceType, setBaselineSourceType] = useState('');
  const [baselineFile, setBaselineFile] = useState(null);
  const [onboardError, setOnboardError] = useState('');
  const [onboardSubmitting, setOnboardSubmitting] = useState(false);
  const [improvementPlanSaving, setImprovementPlanSaving] = useState(false);
  const [improvementPlanError, setImprovementPlanError] = useState('');
  const [runtimeSourceActionLoadingRef, setRuntimeSourceActionLoadingRef] = useState(null);
  const [runtimeSourceActionStatus, setRuntimeSourceActionStatus] = useState('');
  const [deleteDialogSystem, setDeleteDialogSystem] = useState(null);
  const [deleteSubmitting, setDeleteSubmitting] = useState(false);
  const [deleteDialogError, setDeleteDialogError] = useState('');
  const requestCacheNamespace = `${auth?.active_tenant_id || 'anonymous'}:${refreshSeed}`;

  useEffect(() => {
    requestCacheRef.current.clear();
  }, [requestCacheNamespace]);

  useEffect(() => {
    const existingFavicon = document.querySelector("link[rel~='icon']");
    if (existingFavicon) {
      existingFavicon.href = appFavicon;
    } else {
      const faviconLink = document.createElement('link');
      faviconLink.rel = 'icon';
      faviconLink.href = appFavicon;
      document.head.appendChild(faviconLink);
    }

    if (!window.location.hash) {
      window.location.hash = defaultHash();
    }

    const onHashChange = () => setRoute(parseRoute(window.location.hash));
    window.addEventListener('hashchange', onHashChange);
    return () => window.removeEventListener('hashchange', onHashChange);
  }, []);

  useEffect(() => {
    if (!moreNavOpen) {
      return undefined;
    }

    const closeIfOutside = (event) => {
      if (!moreNavRef.current || moreNavRef.current.contains(event.target)) {
        return;
      }
      setMoreNavOpen(false);
    };
    const closeOnEscape = (event) => {
      if (event.key === 'Escape') {
        setMoreNavOpen(false);
      }
    };

    document.addEventListener('pointerdown', closeIfOutside);
    document.addEventListener('keydown', closeOnEscape);
    return () => {
      document.removeEventListener('pointerdown', closeIfOutside);
      document.removeEventListener('keydown', closeOnEscape);
    };
  }, [moreNavOpen]);

  useEffect(() => {
    setMoreNavOpen(false);
  }, [route.name, route.systemId]);

  useEffect(() => {
    let cancelled = false;

    async function loadReleaseMetadata() {
      try {
        const payload = await fetchReleaseMetadata();
        if (!cancelled) {
          setReleaseMetadata(payload);
        }
      } catch {
        if (!cancelled) {
          setReleaseMetadata(null);
        }
      }
    }

    loadReleaseMetadata();
    return () => {
      cancelled = true;
    };
  }, []);

  useEffect(() => {
    let cancelled = false;

    async function loadAuth() {
      setAuthLoading(true);
      try {
        const authPayload = await fetchAuthMe();
        if (cancelled) {
          return;
        }
        setAuth(authPayload);
        setAuthFailure(null);
      } catch (requestError) {
        if (cancelled) {
          return;
        }
        if (isAuthError(requestError)) {
          setAuth(null);
          setAuthFailure(requestError);
        } else {
          setAuth(null);
          setAuthFailure({ status: 500, detail: requestError.message });
        }
      } finally {
        if (!cancelled) {
          setAuthLoading(false);
        }
      }
    }

    loadAuth();
    return () => {
      cancelled = true;
    };
  }, [authRefreshSeed]);

  useEffect(() => {
    if (authLoading) {
      return undefined;
    }

    let cancelled = false;

    async function loadSystemsPortfolio() {
      if (!auth) {
        setSystems([]);
        setSystemsLoading(false);
        setSystemsLoaded(false);
        setSystemsError('');
        return;
      }

      const cachedSystems = readCachedValue(requestCacheRef, requestCacheNamespace, 'systems');
      if (cachedSystems) {
        setSystems(cachedSystems);
        setSystemsLoading(false);
        setSystemsLoaded(true);
        setSystemsError('');
        return;
      }

      setSystemsLoading(true);
      setSystemsError('');
      try {
        const systemsPayload = await loadCachedValue(requestCacheRef, requestCacheNamespace, 'systems', () => fetchSystems());
        if (cancelled) {
          return;
        }
        setSystems(systemsPayload);
        setSystemsLoaded(true);
      } catch (requestError) {
        if (cancelled) {
          return;
        }
        setSystemsLoaded(true);
        if (isAuthError(requestError)) {
          setAuth(null);
          setAuthFailure(requestError);
          setSystems([]);
          setSystemsLoaded(false);
        } else {
          setSystemsError(requestError.message);
        }
      } finally {
        if (!cancelled) {
          setSystemsLoading(false);
        }
      }
    }

    loadSystemsPortfolio();
    return () => {
      cancelled = true;
    };
  }, [auth, authLoading, requestCacheNamespace]);

  useEffect(() => {
    if (authLoading) {
      return undefined;
    }

    if (!auth || !route.systemId) {
      setRouteSystem(null);
      setRouteSystemMissing(false);
      return undefined;
    }

    let cancelled = false;
    const systemCacheKey = `system:${route.systemId}`;
    const cachedSystem = readCachedValue(requestCacheRef, requestCacheNamespace, systemCacheKey);

    if (cachedSystem) {
      setRouteSystem(cachedSystem);
      setRouteSystemMissing(false);
      return undefined;
    }

    setRouteSystem(null);
    setRouteSystemMissing(false);

    async function loadRouteSystem() {
      try {
        const systemPayload = await loadCachedValue(requestCacheRef, requestCacheNamespace, systemCacheKey, () =>
          fetchSystem(route.systemId)
        );
        if (cancelled) {
          return;
        }
        setRouteSystem(systemPayload);
        setRouteSystemMissing(false);
      } catch (requestError) {
        if (cancelled) {
          return;
        }
        if (isAuthError(requestError)) {
          setAuth(null);
          setAuthFailure(requestError);
        } else {
          setRouteSystem(null);
          setRouteSystemMissing(true);
        }
      }
    }

    loadRouteSystem();
    return () => {
      cancelled = true;
    };
  }, [auth, authLoading, requestCacheNamespace, route.systemId]);

  useEffect(() => {
    if (!auth || !onboarding || onboardingStalled) {
      return undefined;
    }
    if (!route.systemId || !ONBOARDING_POLL_ROUTES.has(route.name)) {
      return undefined;
    }
    if (ONBOARDING_TERMINAL_STATUSES.has(onboarding.status)) {
      return undefined;
    }

    let cancelled = false;
    let timeoutId = null;

    const fetchStatus = async () => {
      try {
        const payload = await fetchOnboardingStatus(onboarding.systemId);
        if (cancelled) {
          return;
        }
        const nextStatus = payload.status;
        const changed = onboarding.status !== nextStatus;
        const lastChange = changed ? Date.now() : onboarding.lastStatusChangeAt;
        const elapsedSinceChange = lastChange ? Date.now() - lastChange : 0;
        const stalled = elapsedSinceChange > ONBOARDING_STALL_THRESHOLD_MS;
        setOnboarding((prev) => {
          if (!prev) {
            return prev;
          }
          if (stalled) {
            setOnboardingStalled(true);
          }
          if (changed && nextStatus === 'completed') {
            setRefreshSeed((value) => value + 1);
          }
          if (
            !changed &&
            prev.failureReason === payload.failure_reason &&
            sameJsonValue(prev.mappingContextWarning, payload.mapping_context_warning) &&
            sameJsonValue(prev.duplicateContentWarning, payload.duplicate_content_warning) &&
            sameJsonValue(prev.componentGraphContext, payload.component_graph_context) &&
            prev.lastStatusChangeAt === lastChange
          ) {
            return prev;
          }
          return {
            ...prev,
            status: nextStatus,
            failureReason: payload.failure_reason,
            mappingFingerprint: payload.mapping_fingerprint || prev.mappingFingerprint || null,
            mappingContextWarning: payload.mapping_context_warning || null,
            duplicateContentWarning: payload.duplicate_content_warning || null,
            componentGraphContext: payload.component_graph_context || null,
            lastStatusChangeAt: lastChange,
          };
        });
        if (!ONBOARDING_TERMINAL_STATUSES.has(nextStatus) && !stalled) {
          timeoutId = window.setTimeout(fetchStatus, onboardingPollDelay(elapsedSinceChange));
        }
      } catch (statusError) {
        return;
      }
    };

    fetchStatus();
    return () => {
      cancelled = true;
      if (timeoutId !== null) {
        window.clearTimeout(timeoutId);
      }
    };
  }, [auth, onboarding, onboardingStalled, route]);

  useEffect(() => {
    if (authLoading) {
      return undefined;
    }

    let cancelled = false;

    async function load() {
      setError('');
      setOverview(null);
      setArchitecture(null);
      setComponentsList([]);
      setComponentDetail(null);
      setFindingWorkflows([]);
      setFindingComponents([]);
      setFindingDetail(null);
      setFindingComparison(null);
      setEvalPackDetail(null);
      setRunTrace(null);
      setEvalPackPreview(null);
      setEvalPackPreviewFindingId(null);
      setEvalPackPreviewLoadingFindingId(null);
      setEvalPackActionLoadingFindingId(null);
      setEvalPackActionError('');
      setRecommendationDetail(null);
      setWorkflowSummaries([]);
      setWorkflowDetail(null);
      setRuntimeCoverageBoard(null);
      setCompareSummary(null);
      setCompareFindings([]);
      setCompareWorkflows([]);
      setCompareComponents([]);
      setCompareWarnings([]);
      setMemberships([]);
      setAuditEvents([]);
      setAccessLoading(false);
      setAccessError('');
      if (!route.systemId) {
        setRouteSystem(null);
      }

      if (!auth) {
        setFindings([]);
        setLoading(false);
        return;
      }

      if (route.name === 'systems-list' || route.name === 'systems-onboard') {
        setFindings([]);
        setLoading(false);
        return;
      }

      try {
        if (route.name === 'access') {
          const accessCacheKey = `access:${auth.active_tenant_id}`;
          const cachedAccess = readCachedValue(requestCacheRef, requestCacheNamespace, accessCacheKey);
          if (cachedAccess) {
            setMemberships(cachedAccess.memberships);
            setAuditEvents(cachedAccess.auditEvents);
            setFindings([]);
            setLoading(false);
            return;
          }
          setAccessLoading(true);
          setLoading(true);
          const accessPayload = await loadCachedValue(requestCacheRef, requestCacheNamespace, accessCacheKey, async () => {
            const currentTenantMemberships = auth.memberships.filter((membership) => membership.tenant_id === auth.active_tenant_id);
            const [membershipsPayload, auditPayload] = await Promise.all([
              auth.active_role === 'tenant_admin' ? fetchMemberships(auth.active_tenant_id) : Promise.resolve(currentTenantMemberships),
              fetchAuditEvents({ limit: 20 }),
            ]);
            return { memberships: membershipsPayload, auditEvents: auditPayload };
          });
          if (cancelled) {
            return;
          }
          setMemberships(accessPayload.memberships);
          setAuditEvents(accessPayload.auditEvents);
          setFindings([]);
          return;
        }

        if (!route.systemId) {
          setFindings([]);
          return;
        }

        if (route.name === 'overview') {
          const overviewCacheKey = `overview:${route.systemId}`;
          const cachedOverview = readCachedValue(requestCacheRef, requestCacheNamespace, overviewCacheKey);
          if (cachedOverview) {
            setOverview(cachedOverview);
            setFindings([]);
            setLoading(false);
            return;
          }
          setLoading(true);
          const overviewPayload = await loadCachedValue(requestCacheRef, requestCacheNamespace, overviewCacheKey, () =>
            fetchOverviewData(route.systemId)
          );
          if (cancelled) {
            return;
          }
          setOverview(overviewPayload);
          setFindings([]);
          return;
        }

        if (route.name === 'architecture') {
          const architectureCacheKey = `architecture:${route.systemId}`;
          const cachedArchitecture = readCachedValue(requestCacheRef, requestCacheNamespace, architectureCacheKey);
          if (cachedArchitecture) {
            setArchitecture(cachedArchitecture.architecture);
            setFindings(cachedArchitecture.findings);
            setLoading(false);
            return;
          }
          setLoading(true);
          const architecturePayload = await loadCachedValue(requestCacheRef, requestCacheNamespace, architectureCacheKey, () =>
            fetchArchitectureData(route.systemId)
          );
          if (cancelled) {
            return;
          }
          setArchitecture(architecturePayload.architecture);
          setFindings(architecturePayload.findings);
          return;
        }

        if (route.name === 'compare') {
          const compareCacheKey = `compare:${route.systemId}`;
          const cachedCompare = readCachedValue(requestCacheRef, requestCacheNamespace, compareCacheKey);
          if (cachedCompare) {
            setCompareSummary(cachedCompare.compare.summary);
            setCompareFindings(cachedCompare.compare.findings);
            setCompareWorkflows(cachedCompare.compare.workflows);
            setCompareComponents(cachedCompare.compare.components);
            setCompareWarnings(cachedCompare.warnings || cachedCompare.compare.warnings || []);
            setWorkflowSummaries(cachedCompare.workflowSummaries || []);
            setFindings([]);
            setLoading(false);
            return;
          }
          setLoading(true);
          const comparePayload = await loadCachedValue(requestCacheRef, requestCacheNamespace, compareCacheKey, async () => {
            const [compareWorkspacePayload, workflowSummariesPayload] = await Promise.all([
              fetchCompareWorkspaceData(route.systemId),
              fetchCompareWorkflowSummaries(route.systemId),
            ]);
            return {
              compare: compareWorkspacePayload,
              workflowSummaries: workflowSummariesPayload.value,
              warnings: [...(compareWorkspacePayload.warnings || []), workflowSummariesPayload.warning].filter(Boolean),
            };
          });
          if (cancelled) {
            return;
          }
          setCompareSummary(comparePayload.compare.summary);
          setCompareFindings(comparePayload.compare.findings);
          setCompareWorkflows(comparePayload.compare.workflows);
          setCompareComponents(comparePayload.compare.components);
          setCompareWarnings(comparePayload.warnings || comparePayload.compare.warnings || []);
          setWorkflowSummaries(comparePayload.workflowSummaries || []);
          setFindings([]);
          return;
        }

        if (route.name === 'workflows-list') {
          const workflowSummariesCacheKey = `workflow-summaries:${route.systemId}`;
          const cachedWorkflowSummaries = readCachedValue(requestCacheRef, requestCacheNamespace, workflowSummariesCacheKey);
          if (cachedWorkflowSummaries) {
            setWorkflowSummaries(cachedWorkflowSummaries);
            setFindings([]);
            setLoading(false);
            return;
          }
          setLoading(true);
          const workflowSummariesPayload = await loadCachedValue(
            requestCacheRef,
            requestCacheNamespace,
            workflowSummariesCacheKey,
            () => fetchWorkflowSummariesData(route.systemId)
          );
          if (cancelled) {
            return;
          }
          setWorkflowSummaries(workflowSummariesPayload);
          setFindings([]);
          return;
        }

        if (route.name === 'runtime-coverage') {
          const coverageCacheKey = `runtime-coverage:${route.systemId}:${route.workflowId || ''}:${route.findingStatus || ''}:${route.findingPriority || ''}:${route.coverageStatus || ''}:${route.attentionOnly ? 'attention' : ''}`;
          const cachedCoverageBoard = readCachedValue(requestCacheRef, requestCacheNamespace, coverageCacheKey);
          if (cachedCoverageBoard) {
            setRuntimeCoverageBoard(cachedCoverageBoard);
            setFindings([]);
            setLoading(false);
            return;
          }
          setLoading(true);
          const coverageBoardPayload = await loadCachedValue(requestCacheRef, requestCacheNamespace, coverageCacheKey, () =>
            fetchRuntimeRiskCoverageBoard(route.systemId, route)
          );
          if (cancelled) {
            return;
          }
          setRuntimeCoverageBoard(coverageBoardPayload);
          setFindings([]);
          return;
        }

        if (route.name === 'integrations') {
          const workflowSummariesCacheKey = `workflow-summaries:${route.systemId}`;
          const cachedWorkflowSummaries = readCachedValue(requestCacheRef, requestCacheNamespace, workflowSummariesCacheKey);
          if (cachedWorkflowSummaries) {
            setWorkflowSummaries(cachedWorkflowSummaries);
            setFindings([]);
            setLoading(false);
            return;
          }
          setLoading(true);
          const workflowSummariesPayload = await loadCachedValue(
            requestCacheRef,
            requestCacheNamespace,
            workflowSummariesCacheKey,
            () => fetchWorkflowSummariesData(route.systemId)
          );
          if (cancelled) {
            return;
          }
          setWorkflowSummaries(workflowSummariesPayload);
          setFindings([]);
          return;
        }

        if (route.name === 'workflow-detail') {
          const workflowDetailCacheKey = `workflow-detail:${route.workflowId}`;
          const cachedWorkflowDetail = readCachedValue(requestCacheRef, requestCacheNamespace, workflowDetailCacheKey);
          if (cachedWorkflowDetail) {
            setWorkflowDetail(cachedWorkflowDetail);
            setFindings([]);
            setLoading(false);
            return;
          }
          setLoading(true);
          const workflowDetailPayload = await loadCachedValue(requestCacheRef, requestCacheNamespace, workflowDetailCacheKey, () =>
            fetchWorkflowDetailData(route.workflowId)
          );
          if (cancelled) {
            return;
          }
          setWorkflowDetail(workflowDetailPayload);
          setFindings([]);
          return;
        }

        if (route.name === 'eval-pack-detail') {
          const evalPackCacheKey = `eval-pack:${route.packId}`;
          const cachedEvalPack = readCachedValue(requestCacheRef, requestCacheNamespace, evalPackCacheKey);
          if (cachedEvalPack) {
            setEvalPackDetail(cachedEvalPack);
            setFindings([]);
            setLoading(false);
            return;
          }
          setLoading(true);
          const evalPackPayload = await loadCachedValue(requestCacheRef, requestCacheNamespace, evalPackCacheKey, () =>
            fetchEvalPackDetailData(route.packId)
          );
          if (cancelled) {
            return;
          }
          setEvalPackDetail(evalPackPayload);
          setFindings([]);
          return;
        }

        if (route.name === 'run-trace-detail') {
          const runTraceCacheKey = `run-trace:${route.runId}:${route.primaryStepId || ''}:${(route.comparisonStepIds || []).join(',')}`;
          const sourceFindingCacheKey = route.sourceFindingId ? `finding-detail:${route.systemId}:${route.sourceFindingId}` : null;
          const cachedRunTrace = readCachedValue(requestCacheRef, requestCacheNamespace, runTraceCacheKey);
          const cachedSourceFinding = sourceFindingCacheKey
            ? readCachedValue(requestCacheRef, requestCacheNamespace, sourceFindingCacheKey)
            : null;
          if (cachedRunTrace && (!route.sourceFindingId || cachedSourceFinding)) {
            setRunTrace(cachedRunTrace);
            if (cachedSourceFinding?.detail) {
              setFindingDetail(cachedSourceFinding.detail);
            }
            setFindings([]);
            setLoading(false);
            return;
          }
          setLoading(true);
          const [runTracePayload, sourceFindingPayload] = await Promise.all([
            cachedRunTrace || loadCachedValue(requestCacheRef, requestCacheNamespace, runTraceCacheKey, () => fetchRunTraceData(route.runId)),
            route.sourceFindingId && sourceFindingCacheKey
              ? loadCachedValue(requestCacheRef, requestCacheNamespace, sourceFindingCacheKey, () =>
                  fetchFindingDetailData(route.sourceFindingId)
                ).catch(() => null)
              : Promise.resolve(null),
          ]);
          if (cancelled) {
            return;
          }
          setRunTrace(runTracePayload);
          if (sourceFindingPayload?.detail) {
            setFindingDetail(sourceFindingPayload.detail);
          }
          setFindings([]);
          return;
        }

        if (route.name === 'components-list') {
          const componentsCacheKey = `components:${route.systemId}`;
          const cachedComponents = readCachedValue(requestCacheRef, requestCacheNamespace, componentsCacheKey);
          if (cachedComponents) {
            setComponentsList(cachedComponents);
            setFindings([]);
            setLoading(false);
            return;
          }
          setLoading(true);
          const componentsPayload = await loadCachedValue(requestCacheRef, requestCacheNamespace, componentsCacheKey, () =>
            fetchComponentsData(route.systemId)
          );
          if (cancelled) {
            return;
          }
          setComponentsList(componentsPayload);
          setFindings([]);
          return;
        }

        if (route.name === 'component-detail') {
          const componentDetailCacheKey = `component-detail:${route.systemId}:${route.componentId}`;
          const cachedComponentDetail = readCachedValue(requestCacheRef, requestCacheNamespace, componentDetailCacheKey);
          if (cachedComponentDetail) {
            setComponentDetail(cachedComponentDetail.detail);
            setComponentsList(cachedComponentDetail.components);
            setFindings([]);
            setLoading(false);
            return;
          }
          setLoading(true);
          const componentPayload = await loadCachedValue(requestCacheRef, requestCacheNamespace, componentDetailCacheKey, () =>
            fetchComponentDetailData(route.systemId, route.componentId)
          );
          if (cancelled) {
            return;
          }
          setComponentDetail(componentPayload.detail);
          setComponentsList(componentPayload.components);
          setFindings([]);
          return;
        }

        if (route.name === 'findings-list') {
          const findingsListCacheKey = `findings-list:${route.systemId}:${route.severity || ''}:${route.status || ''}:${route.prevalence || ''}:${route.workflowId || ''}:${route.componentId || ''}`;
          const cachedFindingsList = readCachedValue(requestCacheRef, requestCacheNamespace, findingsListCacheKey);
          if (cachedFindingsList) {
            setFindings(cachedFindingsList.findings);
            setFindingWorkflows(cachedFindingsList.workflows);
            setFindingComponents(cachedFindingsList.components);
            if (!route.severity && !route.status && !route.prevalence && !route.workflowId && !route.componentId) {
              requestCacheRef.current.set(scopedCacheKey(requestCacheNamespace, `system-findings:${route.systemId}`), {
                status: 'resolved',
                value: cachedFindingsList.findings,
              });
            }
            setLoading(false);
            return;
          }
          setLoading(true);
          const findingsPayload = await loadCachedValue(requestCacheRef, requestCacheNamespace, findingsListCacheKey, () =>
            fetchFindingsListData(route.systemId, route)
          );
          if (cancelled) {
            return;
          }
          setFindings(findingsPayload.findings);
          setFindingWorkflows(findingsPayload.workflows);
          setFindingComponents(findingsPayload.components);
          if (!route.severity && !route.status && !route.prevalence && !route.workflowId && !route.componentId) {
            requestCacheRef.current.set(scopedCacheKey(requestCacheNamespace, `system-findings:${route.systemId}`), {
              status: 'resolved',
              value: findingsPayload.findings,
            });
          }
          return;
        }

        if (route.name === 'finding-detail') {
          const findingDetailCacheKey = `finding-detail:${route.systemId}:${route.findingId}`;
          const cachedFindingDetail = readCachedValue(requestCacheRef, requestCacheNamespace, findingDetailCacheKey);
          const cachedSystemFindings = readCachedValue(requestCacheRef, requestCacheNamespace, `system-findings:${route.systemId}`);
          const cachedFindingComparison = readCachedValue(requestCacheRef, requestCacheNamespace, `finding-comparison:${route.findingId}`);
          if (cachedFindingDetail) {
            setFindingDetail(cachedFindingDetail.detail);
            if (cachedSystemFindings) {
              setFindings(cachedSystemFindings);
            }
            if (cachedFindingComparison) {
              setFindingComparison(cachedFindingComparison);
            }
            setLoading(false);
            return;
          }
          setLoading(true);
          const detailPayload = await loadCachedValue(requestCacheRef, requestCacheNamespace, findingDetailCacheKey, () =>
            fetchFindingDetailData(route.findingId)
          );
          if (cancelled) {
            return;
          }
          setFindingDetail(detailPayload.detail);
          if (cachedSystemFindings) {
            setFindings(cachedSystemFindings);
          }
          if (cachedFindingComparison) {
            setFindingComparison(cachedFindingComparison);
          }
          return;
        }

        if (route.name === 'recommendation-detail') {
          const recommendationCacheKey = `recommendation-detail:${route.systemId}:${route.recommendationId}`;
          const cachedRecommendation = readCachedValue(requestCacheRef, requestCacheNamespace, recommendationCacheKey);
          if (cachedRecommendation) {
            setRecommendationDetail(cachedRecommendation.recommendation);
            setFindings(cachedRecommendation.findings);
            setLoading(false);
            return;
          }
          setLoading(true);
          const recommendationPayload = await loadCachedValue(requestCacheRef, requestCacheNamespace, recommendationCacheKey, () =>
            fetchRecommendationDetailData(route.systemId, route.recommendationId)
          );
          if (cancelled) {
            return;
          }
          setRecommendationDetail(recommendationPayload.recommendation);
          setFindings(recommendationPayload.findings);
        }
      } catch (requestError) {
        if (cancelled) {
          return;
        }
        if (isAuthError(requestError)) {
          setAuth(null);
          setAuthFailure(requestError);
        } else if (route.name === 'access') {
          setAccessError(requestError.message);
        } else {
          setError(requestError.message);
        }
      } finally {
        if (!cancelled) {
          setAccessLoading(false);
          setLoading(false);
        }
      }
    }

    load();
    return () => {
      cancelled = true;
    };
  }, [auth, authLoading, requestCacheNamespace, route]);

  useEffect(() => {
    if (authLoading || !auth || route.name !== 'finding-detail' || !route.systemId || !route.findingId || !findingDetail) {
      return undefined;
    }
    if (findingDetail.finding?.id !== route.findingId) {
      return undefined;
    }

    let cancelled = false;
    const systemFindingsCacheKey = `system-findings:${route.systemId}`;
    const findingComparisonCacheKey = `finding-comparison:${route.findingId}`;

    const cachedSystemFindings = readCachedValue(requestCacheRef, requestCacheNamespace, systemFindingsCacheKey);
    if (cachedSystemFindings) {
      setFindings(cachedSystemFindings);
    } else {
      loadCachedValue(requestCacheRef, requestCacheNamespace, systemFindingsCacheKey, () => fetchSystemFindingsData(route.systemId))
        .then((findingsPayload) => {
          if (!cancelled) {
            setFindings(findingsPayload);
          }
        })
        .catch(() => {
          // Supporting counts should not block the finding detail workflow.
        });
    }

    const cachedFindingComparison = readCachedValue(requestCacheRef, requestCacheNamespace, findingComparisonCacheKey);
    if (cachedFindingComparison) {
      setFindingComparison(cachedFindingComparison);
    } else {
      loadCachedValue(requestCacheRef, requestCacheNamespace, findingComparisonCacheKey, () => fetchFindingComparisonData(route.findingId))
        .then((comparisonPayload) => {
          if (!cancelled) {
            setFindingComparison(comparisonPayload);
          }
        })
        .catch(() => {
          // Trend comparison is a supporting section; keep the core review path usable if it is slow or unavailable.
        });
    }

    return () => {
      cancelled = true;
    };
  }, [auth, authLoading, findingDetail, requestCacheNamespace, route.findingId, route.name, route.systemId]);

  const selectedSystem = systems.find((system) => system.id === route.systemId) || routeSystem || null;
  const routeSystemKnownMissing =
    Boolean(auth) &&
    Boolean(route.systemId) &&
    systemsLoaded &&
    !systemsError &&
    routeSystemMissing &&
    !selectedSystem &&
    route.name !== 'systems-list';

  useEffect(() => {
    if (routeSystemKnownMissing) {
      window.location.hash = '#/systems';
    }
  }, [routeSystemKnownMissing]);

  useEffect(() => {
    if (route.name !== 'finding-detail' || loading || error || !route.section) {
      return;
    }
    const target = document.getElementById(route.section);
    if (!target) {
      return;
    }
    target.scrollIntoView({ block: 'start' });
  }, [route.name, route.section, loading, error, findingDetail]);

  const showSystemsLoading =
    systemsLoading && (route.name === 'systems-list' || (Boolean(route.systemId) && !selectedSystem && !error));
  const showSystemsError = Boolean(systemsError) && !error && (route.name === 'systems-list' || (Boolean(route.systemId) && !selectedSystem));
  const showOnboardingBanner =
    onboarding &&
    onboarding.status !== 'completed' &&
    route.systemId &&
    onboarding.systemId === route.systemId &&
    ['overview', 'compare', 'workflows-list', 'workflow-detail', 'runtime-coverage', 'findings-list', 'finding-detail', 'eval-pack-detail', 'run-trace-detail'].includes(route.name);
  const systemFindingsWithComponent = findings.filter((finding) => finding.component || finding.component_id);
  const componentReliabilityIssues = buildComponentReliabilityIssueMap(systemFindingsWithComponent);
  const heroFindingsCount =
    route.name === 'overview'
      ? overview?.posture?.active_finding_count || 0
      : route.name === 'compare'
        ? compareSummary?.findings?.total_count || 0
        : route.name === 'runtime-coverage'
          ? runtimeCoverageBoard?.summary?.active_runtime_risk_count || 0
        : route.systemId
          ? findings.length
          : 0;
  const routesWithFreshSystemFindings = ['architecture', 'findings-list', 'finding-detail', 'recommendation-detail'].includes(route.name);
  const systemContextActiveFindingCount =
    route.name === 'overview' && overview
      ? overview.posture?.active_finding_count ?? null
      : route.name === 'compare' && compareSummary
        ? compareSummary.findings?.total_count ?? null
        : route.name === 'runtime-coverage' && runtimeCoverageBoard
          ? runtimeCoverageBoard.summary?.active_runtime_risk_count ?? null
        : routesWithFreshSystemFindings
          ? findings.length
          : null;
  const findingsValidationNeededCount = ['findings-list', 'finding-detail'].includes(route.name)
    ? findings.filter((finding) => finding.targeted_validation_summary?.eligibility_status === 'eligible').length
    : null;
  const showSystemChrome =
    selectedSystem &&
    route.name !== 'systems-list' &&
    route.name !== 'systems-onboard' &&
    route.name !== 'access' &&
    route.name !== 'how-rti-works';
  const isReviewTargetRoute = ['findings-list', 'finding-detail'].includes(route.name);
  const systemPipelineSteps = showSystemChrome
    ? buildSystemPipelineSteps(selectedSystem, route, {
        activeFindingCount: systemContextActiveFindingCount ?? heroFindingsCount,
        coverageCount: runtimeCoverageBoard?.summary?.active_runtime_risk_count || 0,
      })
    : [];
  const heroComponentsCount = route.name === 'compare' ? compareSummary?.components?.total_count || 0 : architecture?.components?.length || 0;
  const heroWorkflowCount =
    route.name === 'workflows-list'
      ? workflowSummaries.length
      : route.name === 'workflow-detail' && workflowDetail
        ? 1
        : route.name === 'compare'
          ? compareSummary?.workflows?.total_count || 0
          : 0;
  const compareRegressionCount =
    (compareSummary?.findings?.regressed || 0) + (compareSummary?.workflows?.regressed || 0) + (compareSummary?.components?.regressed || 0);
  const activeFilterChips = activeFindingFilterChips(route, findingWorkflows, findingComponents);
  const loginHref = buildLoginHref();
  const authBlocked = !authLoading && !auth;
  const noMembership = authBlocked && authFailure?.status === 403;
  const canOperateRuntimeSources = roleCanOperateRuntimeSources(auth?.active_role);
  const releaseVersion = releaseMetadata?.release_display_version || releaseMetadata?.release_version || 'v0.6.0-alpha';
  const releaseStage = releaseMetadata?.release_stage || 'alpha';
  const releaseCommit = releaseMetadata?.git_commit_short || releaseMetadata?.git_commit_sha?.slice(0, 7) || null;
  const deployedAtLabel = formatReleaseDate(releaseMetadata?.deployed_at);
  const analysisEngineVersion = releaseMetadata?.analysis_engine_version || 'v1.4';
  const showReleaseCommit = Boolean(releaseCommit) && !releaseVersion.includes(releaseCommit);
  const pageMetric =
    route.name === 'systems-list'
      ? null
      : route.name === 'overview'
        ? { label: 'Active findings', value: heroFindingsCount }
        : route.name === 'workflows-list'
          ? { label: 'Workflows', value: heroWorkflowCount }
          : route.name === 'workflow-detail'
            ? { label: 'Runs', value: workflowDetail?.analysis_window?.run_count || 0 }
            : route.name === 'compare'
              ? { label: 'Regressions', value: compareRegressionCount }
              : route.name === 'runtime-coverage'
                ? { label: 'Active risks', value: heroFindingsCount }
                : route.name === 'findings-list' || route.name === 'finding-detail'
                  ? { label: 'Findings', value: heroFindingsCount }
                  : route.name === 'architecture' || route.name === 'components-list' || route.name === 'component-detail'
                    ? { label: 'Components', value: heroComponentsCount }
                    : null;
  const onboardingBanner = showOnboardingBanner ? (
    <OnboardingBanner
      onboarding={onboarding}
      stalled={onboardingStalled}
      onRefresh={() => {
        setOnboardingStalled(false);
      }}
    />
  ) : null;

  const updateFindingsFilter = (key, value) => {
    if (route.name !== 'findings-list' || !route.systemId) {
      return;
    }
    const nextFilters = {
      severity: route.severity,
      status: route.status,
      prevalence: route.prevalence,
      workflowId: route.workflowId,
      componentId: route.componentId,
    };
    nextFilters[key] = value || null;
    window.location.hash = findingsHref(route.systemId, nextFilters);
  };

  const previewEvalPack = async (findingId) => {
    setEvalPackPreviewFindingId(findingId);
    setEvalPackPreview(null);
    setEvalPackPreviewLoadingFindingId(findingId);
    setEvalPackActionError('');
    try {
      const previewPayload = await fetchFindingEvalPackPreview(findingId);
      setEvalPackPreview(previewPayload);
      setEvalPackPreviewFindingId(findingId);
    } catch (requestError) {
      setEvalPackActionError(requestError.message);
    } finally {
      setEvalPackPreviewLoadingFindingId(null);
    }
  };

  const syncEvalPackCaches = (packPayload) => {
    if (!packPayload?.id || !packPayload?.system_id) {
      return;
    }

    requestCacheRef.current.set(scopedCacheKey(requestCacheNamespace, `eval-pack:${packPayload.id}`), {
      status: 'resolved',
      value: packPayload,
    });
    requestCacheRef.current.delete(
      scopedCacheKey(requestCacheNamespace, `finding-detail:${packPayload.system_id}:${packPayload.finding_id}`)
    );
    requestCacheRef.current.delete(scopedCacheKey(requestCacheNamespace, `finding-comparison:${packPayload.finding_id}`));
    requestCacheRef.current.delete(scopedCacheKey(requestCacheNamespace, `system-findings:${packPayload.system_id}`));
    requestCacheRef.current.delete(scopedCacheKey(requestCacheNamespace, `compare:${packPayload.system_id}`));
    requestCacheRef.current.delete(scopedCacheKey(requestCacheNamespace, `workflow-summaries:${packPayload.system_id}`));
    if (packPayload.workflow_id) {
      requestCacheRef.current.delete(scopedCacheKey(requestCacheNamespace, `workflow-detail:${packPayload.workflow_id}`));
    }

    const findingsListPrefix = scopedCacheKey(requestCacheNamespace, `findings-list:${packPayload.system_id}:`);
    Array.from(requestCacheRef.current.keys()).forEach((cacheKey) => {
      if (cacheKey.startsWith(findingsListPrefix)) {
        requestCacheRef.current.delete(cacheKey);
      }
    });
  };

  const createEvalPack = async (findingId) => {
    setEvalPackPreviewFindingId(findingId);
    setEvalPackActionLoadingFindingId(findingId);
    setEvalPackActionError('');
    try {
      const packPayload = await createFindingEvalPack(findingId);
      syncEvalPackCaches(packPayload);
      setEvalPackPreview(null);
      setEvalPackPreviewFindingId(null);
      if (route.systemId) {
        window.location.hash = `#/systems/${route.systemId}/eval-packs/${packPayload.id}`;
      }
    } catch (requestError) {
      setEvalPackActionError(requestError.message);
    } finally {
      setEvalPackActionLoadingFindingId(null);
    }
  };

  const saveImprovementPlan = async (findingId, planId, payload) => {
    setImprovementPlanSaving(true);
    setImprovementPlanError('');
    try {
      if (planId) {
        await updateImprovementPlan(planId, payload);
      } else {
        await createImprovementPlanForFinding(findingId, payload);
      }
      setRefreshSeed((value) => value + 1);
    } catch (requestError) {
      setImprovementPlanError(requestError.message);
    } finally {
      setImprovementPlanSaving(false);
    }
  };

  const handleReplayRuntimeSource = async (sourceConnection) => {
    if (!sourceConnection?.latest_ingestion_id) {
      return;
    }
    const actionRef = `replay:${sourceConnection.id}`;
    setRuntimeSourceActionLoadingRef(actionRef);
    setRuntimeSourceActionStatus('');
    try {
      const replayed = await replayIngestion(sourceConnection.latest_ingestion_id, {
        reason: 'Manual replay requested from the runtime sources panel.',
      });
      setRuntimeSourceActionStatus(`Replayed ${sourceConnection.display_name}. New ingestion ${replayed.id} is queued for processing.`);
      setRefreshSeed((value) => value + 1);
    } catch (requestError) {
      setRuntimeSourceActionStatus(requestError.message);
    } finally {
      setRuntimeSourceActionLoadingRef(null);
    }
  };

  const handleBackfillRuntimeSource = async (sourceConnection) => {
    if (!sourceConnection?.id) {
      return;
    }
    const actionRef = `backfill:${sourceConnection.id}`;
    setRuntimeSourceActionLoadingRef(actionRef);
    setRuntimeSourceActionStatus('');
    try {
      const endDate = new Date(
        sourceConnection.last_success_at || sourceConnection.evidence_posture?.last_collected_at || new Date().toISOString()
      );
      const safeEndDate = Number.isNaN(endDate.getTime()) ? new Date() : endDate;
      const windowMinutes = Math.max(
        1,
        Math.min(
          sourceConnection.collection_window_minutes || sourceConnection.expected_interval_minutes || 1440,
          7 * 24 * 60
        )
      );
      const startDate = new Date(safeEndDate.getTime() - windowMinutes * 60 * 1000);
      const backfillResult = await backfillSourceConnection(sourceConnection.id, {
        start_at: startDate.toISOString(),
        end_at: safeEndDate.toISOString(),
      });
      setRuntimeSourceActionStatus(
        `Requested ${runtimeSourceWindowLabel(sourceConnection)} replay for ${sourceConnection.display_name}: ${backfillResult.created_count} created, ${backfillResult.reused_count} reused.`
      );
      setRefreshSeed((value) => value + 1);
    } catch (requestError) {
      setRuntimeSourceActionStatus(requestError.message);
    } finally {
      setRuntimeSourceActionLoadingRef(null);
    }
  };

  const confirmDeleteSystem = async () => {
    if (!deleteDialogSystem) {
      return;
    }
    setDeleteSubmitting(true);
    setDeleteDialogError('');
    try {
      await deleteSystem(deleteDialogSystem.id);
      setSystems((prev) => prev.filter((item) => item.id !== deleteDialogSystem.id));
      if (route.systemId === deleteDialogSystem.id) {
        setRouteSystem(null);
      }
      setDeleteDialogSystem(null);
      setRefreshSeed((value) => value + 1);
      window.location.hash = '#/systems';
    } catch (deleteError) {
      setDeleteDialogError(deleteError.message);
    } finally {
      setDeleteSubmitting(false);
    }
  };

  const handleOnboardSubmit = async (event) => {
    event.preventDefault();
    setOnboardError('');
    if (!onboardName.trim()) {
      setOnboardError('System name is required.');
      return;
    }
    if (!onboardSourceType) {
      setOnboardError('Format is required.');
      return;
    }
    if (!onboardFile) {
      setOnboardError('Trace file is required.');
      return;
    }
    if (onboardSourceType === 'agent_session_jsonl' && includeBaselineUpload) {
      setOnboardError('Historical baseline upload is not supported for Agent Session JSONL.');
      return;
    }
    if (includeBaselineUpload && !baselineFile) {
      setOnboardError('Baseline trace file is required when historical baseline upload is enabled.');
      return;
    }
    const formData = new FormData();
    formData.append('name', onboardName.trim());
    formData.append('source_type', onboardSourceType);
    if (onboardDescription.trim()) {
      formData.append('description', onboardDescription.trim());
    }
    formData.append('trace_file', onboardFile);
    if (includeBaselineUpload && baselineFile) {
      formData.append('baseline_trace_file', baselineFile);
      formData.append('baseline_source_type', baselineSourceType || onboardSourceType);
    }
    setOnboardSubmitting(true);
    try {
      const payload = await onboardSystem(formData);
      setOnboarding({
        systemId: payload.system_id,
        status: payload.status,
        failureReason: null,
        mappingFingerprint: payload.mapping_fingerprint || null,
        mappingContextWarning: payload.mapping_context_warning || null,
        duplicateContentWarning: payload.duplicate_content_warning || null,
        componentGraphContext: payload.component_graph_context || null,
        lastStatusChangeAt: Date.now(),
        startedAt: Date.now(),
      });
      setOnboardingStalled(false);
      window.location.hash = `#/systems/${payload.system_id}`;
    } catch (submitError) {
      setOnboardError(submitError.message);
    } finally {
      setOnboardSubmitting(false);
    }
  };

  const refreshAccessView = async () => {
    if (!auth) {
      return;
    }
    setAccessLoading(true);
    setAccessError('');
    try {
      const refreshedAuth = await fetchAuthMe();
      setAuth(refreshedAuth);
      const currentTenantMemberships = refreshedAuth.memberships.filter((membership) => membership.tenant_id === refreshedAuth.active_tenant_id);
      const [membershipsPayload, auditPayload] = await Promise.all([
        refreshedAuth.active_role === 'tenant_admin' ? fetchMemberships(refreshedAuth.active_tenant_id) : Promise.resolve(currentTenantMemberships),
        fetchAuditEvents({ limit: 20 }),
      ]);
      requestCacheRef.current.set(scopedCacheKey(requestCacheNamespace, `access:${refreshedAuth.active_tenant_id}`), {
        status: 'resolved',
        value: { memberships: membershipsPayload, auditEvents: auditPayload },
      });
      setMemberships(membershipsPayload);
      setAuditEvents(auditPayload);
    } catch (requestError) {
      setAccessError(requestError.message);
    } finally {
      setAccessLoading(false);
    }
  };

  const clearAccessCache = () => {
    if (!auth) {
      return;
    }
    requestCacheRef.current.delete(scopedCacheKey(requestCacheNamespace, `access:${auth.active_tenant_id}`));
  };

  const handleCreateAlphaTenant = async (payload) => {
    const result = await createAlphaTenant(payload);
    clearAccessCache();
    await refreshAccessView();
    return result;
  };

  const handleCreateMembership = async (payload) => {
    if (!auth) {
      return null;
    }
    const result = await createMembership(auth.active_tenant_id, payload);
    clearAccessCache();
    await refreshAccessView();
    return result;
  };

  const handleUpdateMembership = async (membershipId, payload) => {
    const result = await updateMembership(membershipId, payload);
    clearAccessCache();
    await refreshAccessView();
    return result;
  };

  const handleDeleteMembership = async (membershipId) => {
    const result = await deleteMembership(membershipId);
    clearAccessCache();
    await refreshAccessView();
    return result;
  };

  const handleTenantSwitch = async (tenantId) => {
    if (!auth || tenantId === auth.active_tenant_id) {
      return;
    }
    setTenantSwitching(true);
    setAccessError('');
    setError('');
    try {
      const nextAuth = await switchActiveTenant({ tenant_id: tenantId });
      setAuth(nextAuth);
      setSystems([]);
      setSystemsLoaded(false);
      setRouteSystem(null);
      setRouteSystemMissing(false);
      setOverview(null);
      setArchitecture(null);
      setComponentsList([]);
      setComponentDetail(null);
      setFindings([]);
      setFindingWorkflows([]);
      setFindingComponents([]);
      setFindingDetail(null);
      setFindingComparison(null);
      setEvalPackDetail(null);
      setRunTrace(null);
      setRecommendationDetail(null);
      setWorkflowSummaries([]);
      setWorkflowDetail(null);
      setCompareSummary(null);
      setCompareFindings([]);
      setCompareWorkflows([]);
      setCompareComponents([]);
      setCompareWarnings([]);
      setRefreshSeed((value) => value + 1);
      window.location.hash = '#/systems';
    } catch (requestError) {
      setAccessError(requestError.message);
    } finally {
      setTenantSwitching(false);
    }
  };

  const handleLogout = async () => {
    setLogoutSubmitting(true);
    setAccessError('');
    try {
      const logoutPayload = await logout();
      setAuth(null);
      setAuthFailure({ status: 401, detail: 'Authentication required' });
      setSystems([]);
      setSystemsLoaded(false);
      setRouteSystem(null);
      setRouteSystemMissing(false);
      setMemberships([]);
      setAuditEvents([]);
      if (logoutPayload?.logout_url) {
        window.location.assign(logoutPayload.logout_url);
        return;
      }
    } catch (requestError) {
      setAccessError(requestError.message);
    } finally {
      setLogoutSubmitting(false);
    }
  };

  if (authBlocked && route.name !== 'how-rti-works') {
    return (
      <AuthLandingView
        loginHref={loginHref}
        noMembership={noMembership}
        message={
          noMembership
            ? 'You are signed in, but no Runtime Intelligence tenant is available yet. Contact Agentic Blue to create your workspace.'
            : 'Tenant-scoped runtime evidence · Workspace-only access'
        }
      />
    );
  }

  return (
    <main
      className={[
        'app-shell',
        showSystemChrome ? 'app-shell-with-side-nav' : '',
        isReviewTargetRoute ? 'app-shell-review-target' : '',
      ]
        .filter(Boolean)
        .join(' ')}
    >
      <details className="release-disclosure" aria-label="Alpha release status">
        <summary>
          <span>About this release</span>
          <span className="release-stage">{releaseStage}</span>
          <strong>{releaseVersion}</strong>
        </summary>
        <div className="release-disclosure-body" aria-label="Deployment metadata">
          <span>Updated {deployedAtLabel}</span>
          {showReleaseCommit ? <span>Git {releaseCommit}</span> : null}
          <span>Signal engine {analysisEngineVersion}</span>
        </div>
      </details>

      <section
        className={[
          'panel',
          'compact-page-header',
          route.name === 'access' ? 'compact-page-header-utility' : '',
          isReviewTargetRoute ? 'compact-page-header-review' : '',
        ]
          .filter(Boolean)
          .join(' ')}
      >
        <div className="compact-page-copy">
          {!showSystemChrome ? <img className="brand-logo" src={brandLogoLight} alt="Agentic Blue" /> : null}
          <div>
            <p className="eyebrow">Agentic Blue</p>
            <h1>{routeTitle(route)}</h1>
            <p className="lede">{compactSubtitle(route)}</p>
          </div>
        </div>
        {pageMetric ? (
          <div className="compact-page-metric">
            <CompactMetric label={pageMetric.label} value={pageMetric.value} />
          </div>
        ) : null}
      </section>

      <nav className={showSystemChrome ? 'top-nav panel system-side-nav' : 'top-nav panel'}>
        {showSystemChrome ? (
          <div className="side-nav-brand" aria-label="Agentic Blue">
            <img className="side-nav-brand-logo" src={brandLogoLight} alt="" aria-hidden="true" />
            <span className="side-nav-brand-copy">
              <span className="side-nav-brand-name">Agentic Blue</span>
              <span className="side-nav-brand-product">Runtime Intelligence</span>
            </span>
          </div>
        ) : null}
        <div className="nav-cluster nav-cluster-entry">
          <a className={navLinkClass(route.name === 'systems-list' || route.name === 'systems-onboard')} href="#/systems">
            Systems
          </a>
        </div>

        {selectedSystem ? (
          <div className="nav-cluster" aria-label="Review navigation">
            <a className={navLinkClass(route.name === 'overview')} href={`#/systems/${selectedSystem.id}`}>
              Overview
            </a>
            <a
              className={navLinkClass(
                route.name === 'findings-list' ||
                  route.name === 'finding-detail' ||
                  route.name === 'recommendation-detail' ||
                  route.name === 'eval-pack-detail'
              )}
              href={`#/systems/${selectedSystem.id}/findings`}
            >
              Findings
            </a>
            <a
              className={navLinkClass(route.name === 'workflows-list' || route.name === 'workflow-detail' || route.name === 'run-trace-detail')}
              href={`#/systems/${selectedSystem.id}/workflows`}
            >
              Workflows
            </a>
            <a className={navLinkClass(route.name === 'runtime-coverage')} href={`#/systems/${selectedSystem.id}/runtime-coverage`}>
              Coverage
            </a>
            <a className={navLinkClass(route.name === 'compare')} href={`#/systems/${selectedSystem.id}/compare`}>
              Compare
            </a>
          </div>
        ) : null}

        <div className="nav-cluster nav-cluster-admin">
          <details
            ref={moreNavRef}
            className="more-nav"
            open={moreNavOpen}
            onToggle={(event) => setMoreNavOpen(event.currentTarget.open)}
          >
            <summary
              className={navLinkClass(
                ['architecture', 'components-list', 'component-detail', 'integrations', 'access', 'how-rti-works'].includes(route.name)
              )}
            >
              More
            </summary>
            <div className="more-nav-menu">
              {selectedSystem ? (
                <>
                  <a className={navLinkClass(route.name === 'architecture')} href={`#/systems/${selectedSystem.id}/architecture`}>
                    Architecture
                  </a>
                  <a className={navLinkClass(route.name === 'components-list' || route.name === 'component-detail')} href={`#/systems/${selectedSystem.id}/components`}>
                    Components
                  </a>
                </>
              ) : null}
              {selectedSystem ? (
                <a className={navLinkClass(route.name === 'integrations')} href={`#/systems/${selectedSystem.id}/integrations`}>
                  Integrations
                </a>
              ) : null}
              {auth ? (
                <a className={navLinkClass(route.name === 'access')} href="#/access">
                  Access
                </a>
              ) : null}
              <a className={navLinkClass(route.name === 'how-rti-works')} href="#/how-rti-works">
                How RTI works
              </a>
              {auth ? (
                <button className="nav-link more-nav-action" type="button" onClick={handleLogout} disabled={logoutSubmitting}>
                  {logoutSubmitting ? 'Signing out...' : 'Sign out'}
                </button>
              ) : null}
            </div>
          </details>
        </div>
      </nav>

      {showSystemChrome ? (
        <PipelineStrip
          className={isReviewTargetRoute ? 'pipeline-strip-review' : ''}
          summary="Runtime evidence becomes triage, evaluation coverage, and conservative outcome proof. Scenario review branches from finding detail."
          steps={systemPipelineSteps}
        />
      ) : null}

      {showSystemChrome ? (
        <SystemContextStrip
          selectedSystem={selectedSystem}
          activeFindingCountOverride={systemContextActiveFindingCount}
          validationNeededCountOverride={findingsValidationNeededCount}
          validationMetricLabel={findingsValidationNeededCount === null ? 'Validation needed' : 'Validate next'}
          currentRouteName={route.name}
        />
      ) : null}

      {error ? <StatusCard tone="error">{error}</StatusCard> : null}
      {showSystemsError ? <StatusCard tone="error">{systemsError}</StatusCard> : null}
      {loading ? <StatusCard>Loading data for the current view…</StatusCard> : null}
      {showSystemsLoading ? <StatusCard>Refreshing systems portfolio…</StatusCard> : null}
      {authLoading ? <StatusCard>Loading access context…</StatusCard> : null}
      {!error && route.name === 'how-rti-works' ? <HowRtiWorksView /> : null}

      {!authLoading && auth ? (
        <>
      {!error && route.name === 'systems-list' ? (
        <SystemsPortfolioView
          systems={systems}
          onRequestDelete={(system) => {
            setDeleteDialogError('');
            setDeleteDialogSystem({ id: system.id, name: system.name });
          }}
        />
      ) : null}

      {!loading && !error && route.name === 'access' && auth ? (
        <AccessView
          auth={auth}
          memberships={memberships}
          auditEvents={auditEvents}
          loading={accessLoading}
          error={accessError}
          tenantSwitching={tenantSwitching}
          onSwitchTenant={handleTenantSwitch}
          onRefresh={refreshAccessView}
          onCreateAlphaTenant={handleCreateAlphaTenant}
          onCreateMembership={handleCreateMembership}
          onUpdateMembership={handleUpdateMembership}
          onDeleteMembership={handleDeleteMembership}
        />
      ) : null}

      {deleteDialogSystem ? (
        <DeleteConfirmDialog
          systemName={deleteDialogSystem.name}
          submitting={deleteSubmitting}
          errorMessage={deleteDialogError}
          onCancel={() => {
            if (!deleteSubmitting) {
              setDeleteDialogSystem(null);
              setDeleteDialogError('');
            }
          }}
          onConfirm={confirmDeleteSystem}
        />
      ) : null}

      {!loading && !error && route.name === 'systems-onboard' ? (
        <OnboardingView
          onboardError={onboardError}
          onboardSubmitting={onboardSubmitting}
          onboardName={onboardName}
          setOnboardName={setOnboardName}
          onboardSourceType={onboardSourceType}
          setOnboardSourceType={setOnboardSourceType}
          onboardDescription={onboardDescription}
          setOnboardDescription={setOnboardDescription}
          setOnboardFile={setOnboardFile}
          includeBaselineUpload={includeBaselineUpload}
          setIncludeBaselineUpload={setIncludeBaselineUpload}
          baselineSourceType={baselineSourceType}
          setBaselineSourceType={setBaselineSourceType}
          setBaselineFile={setBaselineFile}
          onSubmit={handleOnboardSubmit}
        />
      ) : null}

      {!loading && !error && route.name === 'overview' && overview && selectedSystem ? (
        <OverviewView
          selectedSystem={selectedSystem}
          overview={overview}
          findingsHref={findingsHref}
          runtimeSourceActionStatus={runtimeSourceActionStatus}
          canOperateRuntimeSources={canOperateRuntimeSources}
          runtimeSourceActionLoadingRef={runtimeSourceActionLoadingRef}
          onReplayRuntimeSource={handleReplayRuntimeSource}
          onBackfillRuntimeSource={handleBackfillRuntimeSource}
          runtimeSourceWindowLabel={runtimeSourceWindowLabel}
          banner={onboardingBanner}
        />
      ) : null}

      {!loading && !error && route.name === 'workflows-list' && selectedSystem ? (
        <WorkflowsListView
          selectedSystem={selectedSystem}
          workflowSummaries={workflowSummaries}
          findingsHref={findingsHref}
          banner={onboardingBanner}
        />
      ) : null}

      {!loading && !error && route.name === 'compare' && selectedSystem ? (
        <CompareView
          selectedSystem={selectedSystem}
          compareSummary={compareSummary}
          findingRows={compareFindings}
          workflowRows={compareWorkflows}
          workflowSummaries={workflowSummaries}
          componentRows={compareComponents}
          warnings={compareWarnings}
          activeTab={route.compareTab || 'findings'}
          canOperateRuntimeSources={canOperateRuntimeSources}
          banner={onboardingBanner}
        />
      ) : null}

      {!loading && !error && route.name === 'runtime-coverage' && runtimeCoverageBoard && selectedSystem ? (
        <RuntimeRiskCoverageBoardView selectedSystem={selectedSystem} board={runtimeCoverageBoard} route={route} />
      ) : null}

      {!loading && !error && route.name === 'workflow-detail' && workflowDetail && selectedSystem ? (
        <WorkflowDetailView
          selectedSystem={selectedSystem}
          workflowDetail={workflowDetail}
          findingsHref={findingsHref}
          evalPackPreview={evalPackPreview}
          previewFindingId={evalPackPreviewFindingId}
          previewLoadingFindingId={evalPackPreviewLoadingFindingId}
          evalPackActionLoadingFindingId={evalPackActionLoadingFindingId}
          evalPackActionError={evalPackActionError}
          onPreviewEvalPack={previewEvalPack}
          onCreateEvalPack={createEvalPack}
          banner={onboardingBanner}
        />
      ) : null}

      {!loading && !error && route.name === 'eval-pack-detail' && evalPackDetail && selectedSystem ? (
        <EvalPackDetailView selectedSystem={selectedSystem} evalPackDetail={evalPackDetail} onPackDetailChange={syncEvalPackCaches} />
      ) : null}

      {!loading && !error && route.name === 'run-trace-detail' && runTrace && selectedSystem ? (
        <RunTraceDetailView
          selectedSystem={selectedSystem}
          runTrace={runTrace}
          primaryStepId={route.primaryStepId}
          comparisonStepIds={route.comparisonStepIds}
          sourceFindingId={route.sourceFindingId}
          tracePack={findingDetail?.suspicious_trace_pack || null}
        />
      ) : null}

      {!loading && !error && route.name === 'architecture' && selectedSystem ? (
        <ArchitectureView
          selectedSystem={selectedSystem}
          architecture={architecture}
          systemFindingsWithComponent={systemFindingsWithComponent}
          componentReliabilityIssues={componentReliabilityIssues}
          displayMode={architectureDisplayMode(architecture)}
          qualityReason={qualityReasonLabel(architecture?.quality?.reason)}
          graph={
            <ArchitectureGraph
              architecture={architecture}
              highlightedComponentId={route.focusComponentId}
              componentReliabilityIssues={componentReliabilityIssues}
            />
          }
        />
      ) : null}

      {!loading && !error && route.name === 'components-list' && selectedSystem ? (
        <ComponentsCatalogView
          selectedSystem={selectedSystem}
          componentsList={componentsList}
          componentMetricValue={componentMetricValue}
        />
      ) : null}

      {!loading && !error && route.name === 'component-detail' && componentDetail && selectedSystem ? (
        <ComponentDetailView
          selectedSystem={selectedSystem}
          componentDetail={componentDetail}
          findingsHref={findingsHref}
          componentMetricValue={componentMetricValue}
        />
      ) : null}

      {!loading && !error && route.name === 'findings-list' && selectedSystem ? (
        <FindingsListView
          selectedSystem={selectedSystem}
          findings={findings}
          route={route}
          findingWorkflows={findingWorkflows}
          findingComponents={findingComponents}
          activeFilterChips={activeFilterChips}
          updateFindingsFilter={updateFindingsFilter}
          banner={onboardingBanner}
        />
      ) : null}

      {!loading && !error && route.name === 'finding-detail' && findingDetail && selectedSystem ? (
        <FindingDetailView
          selectedSystem={selectedSystem}
          findings={findings}
          findingDetail={findingDetail}
          findingComparison={findingComparison}
          improvementPlanSaving={improvementPlanSaving}
          improvementPlanError={improvementPlanError}
          onSaveImprovementPlan={saveImprovementPlan}
          canManageIntegrations={auth?.active_role === 'tenant_admin'}
          section={route.section}
          banner={onboardingBanner}
        />
      ) : null}

      {!loading && !error && route.name === 'integrations' && selectedSystem ? (
        <IntegrationsView
          selectedSystem={selectedSystem}
          activeRole={auth?.active_role}
          canOperateRuntimeSources={canOperateRuntimeSources}
          workflowSummaries={workflowSummaries}
        />
      ) : null}

      {!loading && !error && route.name === 'recommendation-detail' && recommendationDetail && selectedSystem ? (
        <RecommendationDetailView selectedSystem={selectedSystem} recommendationDetail={recommendationDetail} />
      ) : null}
        </>
      ) : null}
    </main>
  );
}

export default App;
